import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { authStore } from '../../api/client';
import { logout as apiLogout } from '../../api/auth';
import BrandLogo from '../ui/BrandLogo';
import { ShellIcon } from './UnifiedPortalShell';
import { formatRole } from '../../utils/formatters';

const NAV_ITEMS = [
  { to: '/dashboard',         label: 'Dashboard',         icon: 'dashboard' },
  { to: '/interviews',        label: 'Interviews',        icon: 'interviews' },
  { to: '/mock-interviews',   label: 'AI Interviews',     icon: 'interviews' },
  { to: '/applications',      label: 'Applications',      icon: 'applications' },
  { to: '/job-openings',      label: 'Job Openings',      icon: 'openings' },
  { to: '/pipeline',          label: 'Pipeline',          icon: 'pipeline' },
  { to: '/candidates',        label: 'Candidates',        icon: 'candidates' },
  { to: '/questions',         label: 'Question Bank',     icon: 'questions' },
  { to: '/coding-challenges', label: 'Coding Challenges', icon: 'questions' },
  { to: '/reports',           label: 'Reports',           icon: 'reports' },
  { to: '/billing',           label: 'Billing',           icon: 'billing' },
  { to: '/orgs',              label: 'Organization',      icon: 'org', roles: ['ORG_ADMIN', 'SUPER_ADMIN'] },
  { to: '/settings',          label: 'Settings',          icon: 'settings' },
];

const PLATFORM_ADMIN_NAV = [
  { to: '/admin/orgs',        label: 'All Organizations', icon: 'org',     roles: ['SUPER_ADMIN'] },
  { to: '/admin/payments',    label: 'Pending Payments',  icon: 'billing', roles: ['SUPER_ADMIN'] },
  { to: '/admin/signatures',  label: 'Process Blocklist', icon: 'reports', roles: ['SUPER_ADMIN'] },
];

function buildSidebarNavItems(role) {
  const base = NAV_ITEMS.filter((item) => !item.roles || item.roles.includes(role));
  const platform = PLATFORM_ADMIN_NAV.filter((item) => !item.roles || item.roles.includes(role));
  const idx = base.findIndex((i) => i.to === '/settings');
  if (idx >= 0 && platform.length > 0) {
    return [...base.slice(0, idx + 1), ...platform, ...base.slice(idx + 1)];
  }
  return [...base, ...platform];
}

const PAGE_TITLES = [
  { match: /^\/dashboard/,         title: 'Dashboard' },
  { match: /^\/interviews/,        title: 'Interviews' },
  { match: /^\/mock-interviews/,   title: 'AI Interviews' },
  { match: /^\/applications/,      title: 'Applications' },
  { match: /^\/pipeline/,          title: 'Pipeline' },
  { match: /^\/candidates/,        title: 'Candidates' },
  { match: /^\/questions/,         title: 'Question Bank' },
  { match: /^\/coding-challenges/, title: 'Coding Challenges' },
  { match: /^\/reports/,           title: 'Reports' },
  { match: /^\/billing/,           title: 'Billing & Credits' },
  { match: /^\/schedules/,         title: 'Scheduled Interviews' },
  { match: /^\/orgs/,              title: 'Organization' },
  { match: /^\/admin\/orgs/,       title: 'All Organizations' },
  { match: /^\/admin\/payments/,   title: 'Pending Payments' },
  { match: /^\/admin\/signatures/, title: 'Process Blocklist' },
  { match: /^\/settings/,          title: 'Settings' },
  { match: /^\/session\//,         title: 'Live Session' },
  { match: /^\/report\//,          title: 'Session Report' },
];

function deriveTitle(pathname) {
  const found = PAGE_TITLES.find((p) => p.match.test(pathname));
  return found?.title || 'Samastix';
}

export default function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = authStore.user;
  const role = user?.role;
  const orgName = user?.organizationName || user?.orgName || 'Workspace';

  const [topSearch, setTopSearch] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const bellRef = useRef(null);

  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('shell:collapsed') === '1';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('shell:collapsed', collapsed ? '1' : '0');
  }, [collapsed]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!bellOpen) return;
    const onClick = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [bellOpen]);

  const title = deriveTitle(location.pathname);
  const isLiveWorkspace = /^\/session\//.test(location.pathname);
  const navItems = buildSidebarNavItems(role);

  const handleGlobalSearch = (e) => {
    e.preventDefault();
    const q = topSearch.trim();
    if (q) {
      navigate(`/candidates?q=${encodeURIComponent(q)}`);
      setTopSearch('');
    }
  };

  const handleSignOut = async () => {
    try {
      if (authStore.refreshToken) {
        await apiLogout({ refreshToken: authStore.refreshToken });
      }
    } catch (err) {
      console.warn('[AppShell] logout failed:', err.message);
    } finally {
      authStore.clear();
      navigate('/login', { replace: true });
    }
  };

  const initials = (() => {
    if (user?.fullName) {
      const parts = user.fullName.trim().split(/\s+/);
      if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      return parts[0].slice(0, 2).toUpperCase();
    }
    if (user?.email) return user.email.slice(0, 2).toUpperCase();
    return 'AD';
  })();

  const sidebarContent = (
    <aside
      className={`sidebar flex flex-col justify-between transition-[width] duration-200 ${
        collapsed ? '!w-[76px] !px-2' : '!w-[250px]'
      }`}
    >
      <div>
        {/* Brand Header */}
        <div className={`pb-5 border-b border-white/[0.08] ${collapsed ? 'flex justify-center' : ''}`}>
          <BrandLogo
            subtitle={orgName}
            collapsed={collapsed}
            light
          />
        </div>

        {/* Navigation list */}
        <nav className="mt-5 space-y-0.5 overflow-y-auto max-h-[calc(100vh-210px)] no-scrollbar">
          {navItems.map((item) => (
            <React.Fragment key={item.to}>
              {item.to === '/admin/orgs' && !collapsed && (
                <p className="px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-[#8593a8]">
                  Platform Admin
                </p>
              )}
              <NavLink
                to={item.to}
                title={collapsed ? item.label : undefined}
                className={({ isActive }) =>
                  [
                    'nav-item',
                    collapsed ? '!justify-center !px-2 !py-2.5' : '',
                    isActive ? 'active' : '',
                  ].join(' ')
                }
              >
                <ShellIcon name={item.icon} className="w-[18px] h-[18px]" />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            </React.Fragment>
          ))}
        </nav>
      </div>

      {/* User Footer Panel */}
      <div className="pt-4 border-t border-white/[0.08]">
        <div className={`flex items-center gap-3 px-1 mb-3 ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-8 h-8 rounded-full bg-[var(--teal-600)] text-white font-semibold text-xs flex items-center justify-center shrink-0 shadow-sm">
            {initials}
          </div>
          {!collapsed && (
            <div className="min-w-0 leading-tight">
              <p className="text-xs font-semibold text-white truncate">
                {user?.fullName || user?.email?.split('@')[0] || 'Administrator'}
              </p>
              <p className="text-[11px] text-[#8593a8] truncate">
                {formatRole(role)}
              </p>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={handleSignOut}
          title={collapsed ? 'Sign out' : undefined}
          className={`btn btn-ghost !text-white !bg-white/[0.06] hover:!bg-white/[0.12] !border-transparent w-full ${
            collapsed ? '!justify-center !px-2' : ''
          }`}
        >
          <ShellIcon name="logout" className="w-4 h-4 text-[#8593a8]" />
          {!collapsed && <span>Sign Out</span>}
        </button>
      </div>
    </aside>
  );

  return (
    <div className="h-screen w-screen flex bg-[var(--canvas)] text-[var(--ink)] overflow-hidden">
      {/* ── Desktop Sidebar Rail ──────────────────────────────────── */}
      <div className="hidden lg:block shrink-0">{sidebarContent}</div>

      {/* ── Mobile Sidebar Drawer ─────────────────────────────────── */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setMobileOpen(false)}
            className="absolute inset-0 bg-[#0e1a30]/60 backdrop-blur-sm"
          />
          <div className="relative w-[260px] max-w-[85vw] h-full z-10 animate-slide-over">
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="absolute top-4 right-4 text-white/70 hover:text-white p-1"
              aria-label="Close menu"
            >
              <ShellIcon name="close" className="w-5 h-5" />
            </button>
            {sidebarContent}
          </div>
        </div>
      )}

      {/* ── Main Workspace Column ─────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Sticky Topbar */}
        <header className="topbar flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="lg:hidden w-9 h-9 rounded-lg flex items-center justify-center text-[var(--ink-soft)] hover:bg-white/60"
              aria-label="Open menu"
            >
              <ShellIcon name="menu" className="w-5 h-5" />
            </button>

            <button
              type="button"
              onClick={() => setCollapsed(!collapsed)}
              className="hidden lg:inline-flex w-8 h-8 rounded-[8px] items-center justify-center text-[var(--ink-soft)] hover:bg-white border border-[var(--line)] shadow-sm transition"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <ShellIcon
                name="chevron"
                className={`w-3.5 h-3.5 transition-transform ${collapsed ? '' : 'rotate-180'}`}
              />
            </button>

            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-[13px] min-w-0">
              <span className="text-[var(--ink-faint)] font-medium truncate">
                {orgName}
              </span>
              <span className="text-[var(--line)]">/</span>
              <span
                className="text-[var(--navy-950)] font-semibold truncate"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                {title}
              </span>
            </div>

            {/* Global search */}
            <form
              onSubmit={handleGlobalSearch}
              className="hidden md:flex flex-1 justify-center max-w-md mx-4"
            >
              <input
                type="search"
                value={topSearch}
                onChange={(e) => setTopSearch(e.target.value)}
                placeholder="Search candidates, sessions, jobs…"
                className="input !py-1.5 !text-xs !rounded-full !bg-white/90"
              />
            </form>
          </div>

          {/* Right actions: Bell + User Pill + Sign Out */}
          <div className="flex items-center gap-3 shrink-0">
            <div className="relative" ref={bellRef}>
              <button
                type="button"
                onClick={() => setBellOpen(!bellOpen)}
                className="w-9 h-9 rounded-[10px] bg-white border border-[var(--line)] flex items-center justify-center text-[var(--ink-soft)] hover:text-[var(--ink)] hover:border-[#cbd5e1] shadow-sm transition-colors"
                aria-label="Notifications"
              >
                <ShellIcon name="bell" className="w-4 h-4" />
              </button>

              {bellOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-[14px] border border-[var(--line)] shadow-pop p-4 z-50 animate-slide-up">
                  <div className="flex items-center justify-between pb-2 border-b border-[var(--line-soft)]">
                    <p className="text-xs font-bold text-[var(--navy-950)]">
                      Notifications
                    </p>
                    <span className="text-[10px] uppercase font-semibold text-[var(--teal-600)]">
                      All clear
                    </span>
                  </div>
                  <p className="text-xs text-[var(--ink-soft)] pt-3">
                    All interview pipelines and proctoring streams running normally.
                  </p>
                </div>
              )}
            </div>

            {/* User pill */}
            <div className="flex items-center gap-2.5 pl-1">
              <div className="w-8 h-8 rounded-full bg-gradient-brand text-white font-semibold text-xs flex items-center justify-center shrink-0 shadow-sm">
                {initials}
              </div>
              <div className="hidden sm:block leading-tight text-left">
                <p className="text-[13px] font-semibold text-[var(--ink)] truncate max-w-[140px]">
                  {user?.fullName || user?.email?.split('@')[0] || 'User'}
                </p>
                <p className="text-[11px] text-[var(--ink-faint)] truncate">
                  {formatRole(role)}
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content View */}
        <main
          className={
            isLiveWorkspace
              ? 'flex-1 overflow-y-auto bg-[#0b1020]'
              : 'flex-1 overflow-y-auto page-pad page-y bg-[var(--canvas)]'
          }
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
